import { Stack } from "@mui/material";
import { GetLifeDetailV2Response, PaymentFrequency, ProductClasType, ProductNameType } from "@repo/api";
import { FC, useMemo } from "react";
import { formatDate, formatDateWithTemplate, generateIntervalDates, NumberToCZK } from "../../helpers";
import { useTypedTranslation } from "../../hooks";
import { InfoList } from "../InfoList";
import { BodyTextM, HeadingM } from "../Typography";
import * as Styled from "./TransactionsTab.styled";

type TransactionsTabHowToPayProps = {
    contract?: GetLifeDetailV2Response;
};

export const TransactionsTabHowToPay: FC<TransactionsTabHowToPayProps> = ({ contract }) => {
    const { t } = useTypedTranslation();

    const isUlOrUlRsk = useMemo(() => {
        return contract?.productClass === ProductClasType.RskUl || contract?.productClass === ProductClasType.Ul;
    }, [contract?.productClass]);

    const isRsk = useMemo(() => {
        return contract?.productClass === ProductClasType.Rsk;
    }, [contract?.productClass]);

    const isUlSingle = contract?.productName === ProductNameType.NnFufiUlSingle;
    const isPseudoRsk = contract?.productClass === ProductClasType.PseudoRsk;

    const frequency = useMemo(() => {
        if (contract?.paymentFrequency && Object.values(PaymentFrequency).includes(contract.paymentFrequency)) {
            return t(`frequencies2.${contract.paymentFrequency}`);
        }
        return "";
    }, [contract?.paymentFrequency, t]);

    const dueDate = useMemo(() => {
        if (contract?.paymentFrequency === PaymentFrequency.Monthly) {
            return t("fufi.howToPay.firstDayOfMonth");
        } else if (contract?.paymentFrequency === PaymentFrequency.Quarterly) {
            const dates = generateIntervalDates(contract.technicalStartDate, "quarter")?.map((d) => formatDateWithTemplate(d, "D.M.", ""));
            if (!dates) return "-";
            return `${t("fufi.howToPay.payYearly")} ${dates.join(", ")}`;
        } else if (contract?.paymentFrequency === PaymentFrequency.HalfYearly) {
            const dates = generateIntervalDates(contract.technicalStartDate, "halfYear")?.map((d) => formatDateWithTemplate(d, "D.M.", ""));
            if (!dates) return "-";
            return `${t("fufi.howToPay.payYearly")} ${dates.join(", ")}`;
        } else if (contract?.paymentFrequency === PaymentFrequency.Yearly) {
            if (!contract?.technicalStartDate) return "-";
            return `${t("fufi.howToPay.payYearly")} ${formatDateWithTemplate(contract.technicalStartDate, "D.M.", "")}`;
        } else {
            if (!contract?.technicalStartDate) return "-";
            return formatDate(contract.technicalStartDate, "");
        }
    }, [contract?.paymentFrequency, contract?.technicalStartDate, t]);

    const howToPayNote = useMemo(() => {
        if (isUlOrUlRsk) return t("fufi.howToPay.howToPayNoteULaRSKUL");
        else if (isRsk) return t("fufi.howToPay.howToPayNoteRSK");
        return null;
    }, [isUlOrUlRsk, isRsk, t]);

    return (
        <Stack>
            <HeadingM>{t("fufi.howToPay.title")}</HeadingM>
            <InfoList
                isLoading={contract === undefined}
                skeletonCount={6}
                showBottomDivider={(isUlOrUlRsk || isRsk) && contract?.doesEmployerContribute}
                information={[
                    ...(!isPseudoRsk
                        ? [
                              {
                                  title: isUlSingle
                                      ? t("fufi.howToPay.amountSinglePremium")
                                      : t("fufi.howToPay.amountWithFrequency"),
                                  description: isUlSingle
                                      ? NumberToCZK(contract?.premiumClient ?? 0)
                                      : `${NumberToCZK(contract?.premiumClient ?? 0)} ${frequency}`,
                                  tooltipInformationText: t("fufi.howToPay.amountWithFrequencyInfo"),
                                  tooltipPlacement: "left" as const,
                              },
                          ]
                        : []),
                    ...(!isUlSingle
                        ? [
                              {
                                  title: t("fufi.howToPay.dueDate"),
                                  description: dueDate,
                              },
                          ]
                        : []),
                    {
                        title: t("fufi.howToPay.accountNumber"),
                        description: contract?.paymentDetails
                            ? `${contract.paymentDetails.bankAccount}/${contract.paymentDetails.bankCode}`
                            : "",
                    },
                    {
                        title: t("fufi.howToPay.variableSymbol"),
                        description: contract?.paymentDetails?.variableSymbol ?? "",
                    },
                    ...(isUlOrUlRsk
                        ? [
                              {
                                  title: t("fufi.howToPay.specificSymbol"),
                                  description: "11",
                              },
                              {
                                  title: t("fufi.howToPay.specificSymbolExtraordinary"),
                                  description: "22",
                              },
                          ]
                        : []),
                ]}
                additionalBottomContent={
                    (isUlOrUlRsk || isRsk) && contract?.doesEmployerContribute ? (
                        <Styled.TransactionsTabHowToPayBottomStack>
                            <BodyTextM>{howToPayNote}</BodyTextM>
                        </Styled.TransactionsTabHowToPayBottomStack>
                    ) : undefined
                }
            />
        </Stack>
    );
};
